import { inject, Injectable } from '@angular/core';
import { FIRESTORE } from '../../fireauth-lib.config';
import { 
  collection, doc, getDoc, getDocs, setDoc, query, where, 
  Timestamp 
} from 'firebase/firestore';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

// Interfaces for type safety
export interface Organization {
  orgCode: string;
  orgName: string;
  createdBy?: string;
  createdAt: Timestamp;
}

export interface PostType {
  postType: string;
  createdAt: Timestamp;
}

export interface BlogPost {
  id: string;
  title: string;
  content: string;
  coverImageUrl: string;
  coverImageName: string;
  slug: string;
  publishedOn?: Timestamp;
  isPublished?: boolean;
  publishedType?: string;
  publishedBy?: string;
  userId: string;
  tags?: string[];
  formattedMedia?: { [key: string]: string };
  createdOn: Timestamp;
  createdBy: string;
  createdByName?: string;
  createdByOrg: string;
  postType?: string;
  groupAssigned?: string[] | string;
  addTableOfContent?: boolean;
  authorId?: string
}

// New interface for post statistics
export interface PostStatistics {
  totalPosts: number;
  totalPostTypes: number;
  postsByType: Record<string, number>;
  postsByCurrentUser: number;
  totalAuthors: number;
  totalPublishedPosts: number;
}

@Injectable({
  providedIn: 'root'
})
export class BlogPostStoreService {
  private firestore = inject(FIRESTORE);
  
  // Cache storage
  private organizationCache = new Map<string, Organization>();
  private postTypeCache = new Map<string, Map<string, PostType>>();
  private blogPostCache = new Map<string, Map<string, Map<string, BlogPost>>>();
  
  // Observable caches for reactive data
  private organizationsSubject = new BehaviorSubject<Map<string, Organization>>(new Map());
  private postTypesSubject = new Map<string, BehaviorSubject<Map<string, PostType>>>();
  private blogPostsSubject = new Map<string, Map<string, BehaviorSubject<Map<string, BlogPost>>>>();
  
  constructor() { }

  // Organization Methods
  async storeOrganization(organization: Organization): Promise<void> {
    try {
      const orgRef = doc(this.firestore, `Organizations/${organization.orgCode}`);
      await setDoc(orgRef, organization);
      
      // Update cache
      this.organizationCache.set(organization.orgCode, organization);
      this.organizationsSubject.next(this.organizationCache);
    } catch (error) {
      console.error('Error storing organization:', error);
      throw error;
    }
  }

  async getOrganization(orgCode: string): Promise<Organization | null> {
    // Check cache first
    if (this.organizationCache.has(orgCode)) {
      return this.organizationCache.get(orgCode) || null;
    }
    
    try {
      const orgRef = doc(this.firestore, `Organizations/${orgCode}`);
      const orgSnapshot = await getDoc(orgRef);
      
      if (orgSnapshot.exists()) {
        const organization = orgSnapshot.data() as Organization;
        
        // Update cache
        this.organizationCache.set(orgCode, organization);
        this.organizationsSubject.next(this.organizationCache);
        
        return organization;
      }
      return null;
    } catch (error) {
      console.error('Error getting organization:', error);
      throw error;
    }
  }

  // Post Type Methods
  async storePostType(orgCode: string, postTypeData: PostType): Promise<void> {
    try {
      const postTypeRef = doc(
        this.firestore, 
        `Organizations/${orgCode}/PostType/${postTypeData.postType}`
      );
      await setDoc(postTypeRef, postTypeData);
      
      // Update cache
      if (!this.postTypeCache.has(orgCode)) {
        this.postTypeCache.set(orgCode, new Map());
      }
      this.postTypeCache.get(orgCode)?.set(postTypeData.postType, postTypeData);
      
      // Update subject
      if (!this.postTypesSubject.has(orgCode)) {
        this.postTypesSubject.set(orgCode, new BehaviorSubject(new Map()));
      }
      
      // Important: Create a new reference to force change detection
      const updatedMap = new Map(this.postTypeCache.get(orgCode) || new Map());
      this.postTypesSubject.get(orgCode)?.next(updatedMap);
      
      // Reload the list after storing to ensure consistency
      this.getPostTypesByOrgCode(orgCode).catch(error => {
        console.error('Error reloading post types after adding new one:', error);
      });
    } catch (error) {
      console.error('Error storing post type:', error);
      throw error;
    }
  }

  async getPostType(orgCode: string, postType: string): Promise<PostType | null> {
    // Check cache first
    if (this.postTypeCache.has(orgCode) && this.postTypeCache.get(orgCode)?.has(postType)) {
      return this.postTypeCache.get(orgCode)?.get(postType) || null;
    }
    
    try {
      const postTypeRef = doc(
        this.firestore, 
        `Organizations/${orgCode}/PostType/${postType}`
      );
      const postTypeSnapshot = await getDoc(postTypeRef);
      
      if (postTypeSnapshot.exists()) {
        const postTypeData = postTypeSnapshot.data() as PostType;
        
        // Update cache
        if (!this.postTypeCache.has(orgCode)) {
          this.postTypeCache.set(orgCode, new Map());
        }
        this.postTypeCache.get(orgCode)?.set(postType, postTypeData);
        
        // Update subject
        if (!this.postTypesSubject.has(orgCode)) {
          this.postTypesSubject.set(orgCode, new BehaviorSubject(new Map()));
        }
        this.postTypesSubject.get(orgCode)?.next(this.postTypeCache.get(orgCode) || new Map());
        
        return postTypeData;
      }
      return null;
    } catch (error) {
      console.error('Error getting post type:', error);
      throw error;
    }
  }

  // Blog Post Methods
  async storeBlogPost(orgCode: string, postType: string, blogPostId: string, blogPost: BlogPost): Promise<void> {
    try {
      // Generate slug if not provided
      if (!blogPost.slug) {
        blogPost.slug = this.generateSlug(blogPost.title);
      }
      
      const blogPostRef = doc(
        this.firestore, 
        `Organizations/${orgCode}/PostType/${postType}/BlogPosts/${blogPostId}`
      );
      await setDoc(blogPostRef, blogPost);
      
      // Update cache
      this.updateBlogPostCache(orgCode, postType, blogPostId, blogPost);
    } catch (error) {
      console.error('Error storing blog post:', error);
      throw error;
    }
  }

  async getBlogPostsByOrgCode(orgCode: string): Promise<BlogPost[]> {
    try {
      const postTypesSnapshot = await getDocs(collection(this.firestore, `Organizations/${orgCode}/PostType`));
      const blogPosts: BlogPost[] = [];
      
      for (const postTypeDoc of postTypesSnapshot.docs) {
        //console.log('Post Type:', postTypeDoc.id);
        const postType = postTypeDoc.id;
        const blogPostsSnapshot = await getDocs(
          collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`)
        );
        
        for (const blogPostDoc of blogPostsSnapshot.docs) {
          const blogPost = blogPostDoc.data() as BlogPost;
          blogPosts.push(blogPost);
          
          // Update cache
          this.updateBlogPostCache(orgCode, postType, blogPostDoc.id, blogPost);
        }
      }
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting blog posts by org code:', error);
      throw error;
    }
  }

  async getBlogPostsByOrgCodeAndPostType(orgCode: string, postType: string): Promise<BlogPost[]> {
    try {
      const blogPostsSnapshot = await getDocs(
        collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`)
      );
      
      const blogPosts: BlogPost[] = [];
      blogPostsSnapshot.forEach(doc => {
        const blogPost = doc.data() as BlogPost;
        blogPosts.push(blogPost);
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, doc.id, blogPost);
      });
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting blog posts by org code and post type:', error);
      throw error;
    }
  }

  async getBlogPostsByOrgCodePostTypeAndUserId(orgCode: string, postType: string, userId: string): Promise<BlogPost[]> {
    try {
      const blogPostsRef = collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`);
      const q = query(blogPostsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      const blogPosts: BlogPost[] = [];
      querySnapshot.forEach(doc => {
        const blogPost = doc.data() as BlogPost;
        blogPosts.push(blogPost);
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, doc.id, blogPost);
      });
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting blog posts by org code, post type and user ID:', error);
      throw error;
    }
  }

  async getBlogPostByOrgCodePostTypeAndBlogPostId(orgCode: string, postType: string, blogPostId: string): Promise<BlogPost | null> {
    // Check cache first
    if (this.blogPostCache.has(orgCode) && 
        this.blogPostCache.get(orgCode)?.has(postType) && 
        this.blogPostCache.get(orgCode)?.get(postType)?.has(blogPostId)) {
      return this.blogPostCache.get(orgCode)?.get(postType)?.get(blogPostId) || null;
    }
    
    try {
      const blogPostRef = doc(
        this.firestore, 
        `Organizations/${orgCode}/PostType/${postType}/BlogPosts/${blogPostId}`
      );
      const blogPostSnapshot = await getDoc(blogPostRef);
      
      if (blogPostSnapshot.exists()) {
        const blogPost = blogPostSnapshot.data() as BlogPost;
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, blogPostId, blogPost);
        
        return blogPost;
      }
      return null;
    } catch (error) {
      console.error('Error getting blog post by ID:', error);
      throw error;
    }
  }

  async getBlogPostByOrgCodePostTypeUserIdAndBlogPostId(
    orgCode: string, 
    postType: string, 
    userId: string, 
    blogPostId: string
  ): Promise<BlogPost | null> {
    const blogPost = await this.getBlogPostByOrgCodePostTypeAndBlogPostId(orgCode, postType, blogPostId);
    if (blogPost && blogPost.userId === userId) {
      return blogPost;
    }
    return null;
  }

  async getBlogPostByOrgCodePostTypeAndSlug(orgCode: string, postType: string, slug: string): Promise<BlogPost | null> {
    try {
      const blogPostsRef = collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`);
      const q = query(blogPostsRef, where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const blogPostDoc = querySnapshot.docs[0];
        const blogPost = blogPostDoc.data() as BlogPost;
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, blogPostDoc.id, blogPost);
        
        return blogPost;
      }
      return null;
    } catch (error) {
      console.error('Error getting blog post by slug:', error);
      throw error;
    }
  }

  // New methods for published blog posts
  async getPublishedBlogPostsByOrgCode(orgCode: string): Promise<BlogPost[]> {
    try {
      const allBlogPosts = await this.getBlogPostsByOrgCode(orgCode);
      return allBlogPosts.filter(post => post.isPublished);
    } catch (error) {
      console.error('Error getting published blog posts by org code:', error);
      throw error;
    }
  }

  async getPublishedBlogPostsByOrgCodeAndPostType(orgCode: string, postType: string): Promise<BlogPost[]> {
    try {
      const blogPostsRef = collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`);
      const q = query(blogPostsRef, where('isPublished', '==', true));
      const querySnapshot = await getDocs(q);
      
      const blogPosts: BlogPost[] = [];
      querySnapshot.forEach(doc => {
        const blogPost = doc.data() as BlogPost;
        blogPosts.push(blogPost);
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, doc.id, blogPost);
      });
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting published blog posts by org code and post type:', error);
      throw error;
    }
  }

  async getPublishedBlogPostsByOrgCodePostTypeAndUserId(
    orgCode: string, 
    postType: string, 
    userId: string
  ): Promise<BlogPost[]> {
    try {
      const blogPostsRef = collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`);
      const q = query(
        blogPostsRef, 
        where('userId', '==', userId),
        where('isPublished', '==', true)
      );
      const querySnapshot = await getDocs(q);
      
      const blogPosts: BlogPost[] = [];
      querySnapshot.forEach(doc => {
        const blogPost = doc.data() as BlogPost;
        blogPosts.push(blogPost);
        
        // Update cache
        this.updateBlogPostCache(orgCode, postType, doc.id, blogPost);
      });
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting published blog posts by org code, post type and user ID:', error);
      throw error;
    }
  }

  /**
   * Gets all blog posts for a specific organization and user
   * @param orgCode Organization code
   * @param userId User ID to filter posts by
   * @returns Promise with array of blog posts
   */
  async getBlogPostsByOrgCodeAndUserId(orgCode: string, userId: string): Promise<BlogPost[]> {
    try {
      const postTypesSnapshot = await getDocs(collection(this.firestore, `Organizations/${orgCode}/PostType`));
      const blogPosts: BlogPost[] = [];
      
      for (const postTypeDoc of postTypesSnapshot.docs) {
        const postType = postTypeDoc.id;
        const blogPostsRef = collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`);
        const q = query(blogPostsRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach(doc => {
          const blogPost = doc.data() as BlogPost;
          blogPosts.push(blogPost);
          
          // Update cache
          this.updateBlogPostCache(orgCode, postType, doc.id, blogPost);
        });
      }
      
      return blogPosts;
    } catch (error) {
      console.error('Error getting blog posts by org code and user ID:', error);
      throw error;
    }
  }

  async updateBlogPost(orgCode: string, postType: string, blogPostId: string, updates: Partial<BlogPost>): Promise<void> {
    try {
      // Get the current blog post first
      const currentBlogPost = await this.getBlogPostByOrgCodePostTypeAndBlogPostId(orgCode, postType, blogPostId);
      
      if (!currentBlogPost) {
        throw new Error(`Blog post with ID ${blogPostId} not found`);
      }
      
      // If the title is being updated, regenerate the slug
      if (updates.title && updates.title !== currentBlogPost.title) {
        updates.slug = this.generateSlug(updates.title);
      }
      
      // Merge current blog post with updates
      const updatedBlogPost = { ...currentBlogPost, ...updates };
      
      // Use the same reference as in storeBlogPost
      const blogPostRef = doc(
        this.firestore, 
        `Organizations/${orgCode}/PostType/${postType}/BlogPosts/${blogPostId}`
      );
      
      //console.log('Updating blog post:', updatedBlogPost);
      // Update the document in Firestore
      await setDoc(blogPostRef, updatedBlogPost);
      
      // Update cache
      this.updateBlogPostCache(orgCode, postType, blogPostId, updatedBlogPost);
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive statistics about blog posts for an organization
   * @param orgCode Organization code
   * @param currentUserId Current user ID for filtering user-specific stats
   * @returns Promise with post statistics
   */
  async getPostStatistics(orgCode: string, currentUserId: string): Promise<PostStatistics> {
    try {
      // Initialize statistics object
      const stats: PostStatistics = {
        totalPosts: 0,
        totalPostTypes: 0,
        postsByType: {},
        postsByCurrentUser: 0,
        totalAuthors: 0,
        totalPublishedPosts: 0
      };
      
      // Get all post types first
      const postTypesSnapshot = await getDocs(collection(this.firestore, `Organizations/${orgCode}/PostType`));
      
      // Set total post types
      stats.totalPostTypes = postTypesSnapshot.size;
      
      // Set to track unique authors
      const uniqueAuthors = new Set<string>();
      
      // Process each post type
      for (const postTypeDoc of postTypesSnapshot.docs) {
        const postType = postTypeDoc.id;
        const blogPostsSnapshot = await getDocs(
          collection(this.firestore, `Organizations/${orgCode}/PostType/${postType}/BlogPosts`)
        );
        
        // Count posts for this type
        const postsCount = blogPostsSnapshot.size;
        stats.postsByType[postType] = postsCount;
        stats.totalPosts += postsCount;
        
        // Process each blog post for additional stats
        blogPostsSnapshot.forEach(doc => {
          const post = doc.data() as BlogPost;
          
          // Count current user's posts
          if (post.userId === currentUserId) {
            stats.postsByCurrentUser++;
          }
          
          // Track unique authors
          uniqueAuthors.add(post.userId);
          
          // Count published posts
          if (post.isPublished) {
            stats.totalPublishedPosts++;
          }
          
          // Update cache while we're at it
          this.updateBlogPostCache(orgCode, postType, doc.id, post);
        });
      }
      
      // Set total unique authors
      stats.totalAuthors = uniqueAuthors.size;
      
      return stats;
    } catch (error) {
      console.error('Error getting post statistics:', error);
      throw error;
    }
  }
  
  /**
   * Get post statistics as an Observable
   * @param orgCode Organization code
   * @param currentUserId Current user ID
   * @returns Observable of post statistics
   */
  getPostStatisticsObservable(orgCode: string, currentUserId: string): Observable<PostStatistics> {
    return from(this.getPostStatistics(orgCode, currentUserId)).pipe(
      shareReplay(1)  // Cache the result
    );
  }

  // Helper methods
  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s]/gi, '')  // Remove special characters
      .replace(/\s+/g, '-')      // Replace spaces with hyphens
      .replace(/-+/g, '-')       // Replace multiple hyphens with single hyphen
      .trim();
  }

  private updateBlogPostCache(orgCode: string, postType: string, blogPostId: string, blogPost: BlogPost): void {
    // Ensure maps exist at each level
    if (!this.blogPostCache.has(orgCode)) {
      this.blogPostCache.set(orgCode, new Map());
    }
    
    if (!this.blogPostCache.get(orgCode)?.has(postType)) {
      this.blogPostCache.get(orgCode)?.set(postType, new Map());
    }
    
    // Set blog post in cache
    this.blogPostCache.get(orgCode)?.get(postType)?.set(blogPostId, blogPost);
    
    // Update reactive subjects
    if (!this.blogPostsSubject.has(orgCode)) {
      this.blogPostsSubject.set(orgCode, new Map());
    }
    
    if (!this.blogPostsSubject.get(orgCode)?.has(postType)) {
      this.blogPostsSubject.get(orgCode)?.set(postType, new BehaviorSubject(new Map()));
    }
    
    // Get the current map from the subject, update it, and emit the new value
    const currentMap = this.blogPostsSubject.get(orgCode)?.get(postType)?.value || new Map();
    currentMap.set(blogPostId, blogPost);
    this.blogPostsSubject.get(orgCode)?.get(postType)?.next(currentMap);
  }

  // Observable methods for reactive applications
  getOrganizationObservable(orgCode: string): Observable<Organization | null> {
    // Check cache first
    if (this.organizationCache.has(orgCode)) {
      return of(this.organizationCache.get(orgCode) || null);
    }
    
    // Fetch from Firestore and return as observable
    return from(this.getOrganization(orgCode)).pipe(
      shareReplay(1)  // Cache the result
    );
  }

  // Observable methods to get PostTypes
  getPostTypesObservable(orgCode: string): Observable<PostType[]> {
    // Set up the subject if it doesn't exist
    if (!this.postTypesSubject.has(orgCode)) {
      this.postTypesSubject.set(orgCode, new BehaviorSubject(new Map()));
      
      // Load data if the subject is new
      this.getPostTypesByOrgCode(orgCode).catch(error => {
        console.error('Error fetching post types for observable:', error);
      });
    }
    
    // Return the subject as an observable
    return this.postTypesSubject.get(orgCode)?.pipe(
      map(postTypesMap => Array.from(postTypesMap.values()))
    ) || of([]);
  }

  // Helper method to load post types by org code
  private async getPostTypesByOrgCode(orgCode: string): Promise<void> {
    try {
      const postTypesSnapshot = await getDocs(collection(this.firestore, `Organizations/${orgCode}/PostType`));
      
      // Create a new map to ensure reference change
      const newPostTypeMap = new Map<string, PostType>();
      
      postTypesSnapshot.forEach(doc => {
        const postType = doc.data() as PostType;
        
        // Add to our new map
        newPostTypeMap.set(postType.postType, postType);
      });
      
      // Update cache with the complete new map
      this.postTypeCache.set(orgCode, newPostTypeMap);
      
      // Update subject with the new reference
      if (!this.postTypesSubject.has(orgCode)) {
        this.postTypesSubject.set(orgCode, new BehaviorSubject(new Map()));
      }
      this.postTypesSubject.get(orgCode)?.next(newPostTypeMap);
    } catch (error) {
      console.error('Error getting post types by org code:', error);
      throw error;
    }
  }

  getBlogPostsObservable(orgCode: string, postType: string): Observable<BlogPost[]> {
    // Set up the subject if it doesn't exist
    if (!this.blogPostsSubject.has(orgCode)) {
      this.blogPostsSubject.set(orgCode, new Map());
    }
    
    if (!this.blogPostsSubject.get(orgCode)?.has(postType)) {
      this.blogPostsSubject.get(orgCode)?.set(postType, new BehaviorSubject(new Map()));
      
      // Load data if the subject is new
      this.getBlogPostsByOrgCodeAndPostType(orgCode, postType).catch(error => {
        console.error('Error fetching blog posts for observable:', error);
      });
    }
    
    // Return the subject as an observable
    return this.blogPostsSubject.get(orgCode)?.get(postType)?.pipe(
      map(postsMap => Array.from(postsMap.values()))
    ) || of([]);
  }

  getBlogPostsByOrgCodeAndUserIdObservable(orgCode: string, userId: string): Observable<BlogPost[]> {
    // Fetch the data and return as observable with caching
    return from(this.getBlogPostsByOrgCodeAndUserId(orgCode, userId)).pipe(
      shareReplay(1)  // Cache the result
    );
  }

  // Observable methods for published blog posts
  getPublishedBlogPostsObservable(orgCode: string, postType: string): Observable<BlogPost[]> {
    return this.getBlogPostsObservable(orgCode, postType).pipe(
      map(posts => posts.filter(post => post.isPublished))
    );
  }

  // Observable methods for published blog posts
  getInternalBlogPostsByOrgCodeAndPostTypeObservable(orgCode: string, postType: string): Observable<BlogPost[]> {
    return this.getBlogPostsObservable(orgCode, postType).pipe(
      map(posts => posts.filter(post => post.publishedType === 'internal'))
    );
  }
  
  getPublicBlogPostsByOrgCodeAndPostTypeObservable(orgCode: string, postType: string): Observable<BlogPost[]> {
    return this.getBlogPostsObservable(orgCode, postType).pipe(
      map(posts => posts.filter(post => post.publishedType === 'public'))
    );
  }

  getInternalBlogPostsByOrgCodeObservable(orgCode: string): Observable<BlogPost[]> {
    return this.getBlogPostsByOrgCodeObservable(orgCode).pipe(
      map(posts => posts.filter(post => post.publishedType === 'internal'))
    );
  }
  
  getPublicBlogPostsByOrgCodeObservable(orgCode: string): Observable<BlogPost[]> {
    return this.getBlogPostsByOrgCodeObservable(orgCode).pipe(
      map(posts => posts.filter(post => post.publishedType === 'public'))
    );
  }
  
  /**
   * Get all blog posts for an organization as an Observable
   * @param orgCode Organization code
   * @returns Observable of BlogPost array with caching
   */
  getBlogPostsByOrgCodeObservable(orgCode: string): Observable<BlogPost[]> {
    // Create a cached observable using the async method
    return from(this.getBlogPostsByOrgCode(orgCode)).pipe(
      shareReplay(1)  // Cache the result
    );
  }
}
